import { ApiError, isForbidden } from '@cwos/api-client';
import type { timekeeping } from '@cwos/api-client';
import { narrowedTo, ORGANIZATION, UNKNOWN_POPULATION, type Population } from '../figureState.js';
import type { ScopeReader } from '../readScope.js';

/**
 * Pure logic for TIME CORRECTIONS — filing one, and deciding one.
 *
 * `POST /timekeeping/adjustments` and `POST /timekeeping/adjustments/:id/decision`
 * have existed since Task 5 and had zero callers in any UI. So a punch recorded
 * wrong could not be fixed through the product at all: not by the worker, not by
 * the supervisor, not by the office. Payroll's only recourse was the database.
 *
 * Everything here is DOM-free, for the same reason `timeLogic.ts` is — the rules
 * on this surface are the server's rules, and they have to be testable against
 * the server's rules rather than against a rendering of them.
 *
 * WHAT IS DELIBERATELY NOT HERE: any re-derivation of server scope. The decision
 * route applies three gates (the permission, `requireOwnWorkerToWrite` over the
 * SUBJECT worker, and separation of duties), and only the first is knowable from
 * the caller's own permission set. F35 — `features/right-now` re-deriving server
 * scope client-side off a flattened key list — is the open finding that says why
 * guessing the other two would be the bug rather than the feature. So the
 * control is shown to a `timekeeping:manage` holder and the server's refusal is
 * rendered as a refusal when it comes. See {@link decisionOutcome}.
 */

// --- what the caller may do -------------------------------------------------

/**
 * MAY THIS PRINCIPAL SEE THE CORRECTION QUEUE?
 *
 * `GET /timekeeping/adjustments` is `guard(request, reply, 'time:read')`
 * (apps/api/src/domains/timekeeping/routes.ts:561). Every seeded role holds
 * `time:read`, so this is rarely false — but it gates the `enabled:` of the
 * query so an unheld key produces no request and no cached 403.
 */
export const CORRECTIONS_READ = 'time:read';

export function mayReadCorrections(has: (key: string | undefined) => boolean): boolean {
  return has(CORRECTIONS_READ);
}

/**
 * MAY THIS PRINCIPAL DECIDE A CORRECTION?
 *
 * `timekeeping:manage` — routes.ts:588. This is the DOOR and not the whole
 * answer: reach over the subject worker and separation of duties are checked
 * afterwards, per request, against facts the client does not hold. A holder of
 * this key is shown the controls; a holder who is refused a particular request
 * is told so on that request.
 */
export const CORRECTIONS_DECIDE = 'timekeeping:manage';

export function mayDecideCorrections(has: (key: string | undefined) => boolean): boolean {
  return has(CORRECTIONS_DECIDE);
}

/**
 * MAY THIS PRINCIPAL FILE A CORRECTION?
 *
 * `time:adjust` — routes.ts:511. Held by org_admin, supervisor AND worker
 * (1755064800000_time-clock.sql:346,357,381).
 *
 * Whether they may file it about a GIVEN worker is `requireOwnWorkerToWrite`,
 * which is the supervisor edge or `timekeeping:write_all`, and is not derivable
 * here. A form offered on this key alone will sometimes meet a 403, and that
 * 403 is the honest answer rather than a failure to predict it.
 */
export function mayFileCorrections(has: (key: string | undefined) => boolean): boolean {
  return has('time:adjust');
}

/**
 * WHOSE CORRECTIONS THE UNFILTERED QUEUE IS SHOWING.
 *
 * `GET /timekeeping/adjustments` called without a `workerId` does NOT refuse a
 * narrow reader — it scopes in the SQL, and the route says so: "the unfiltered
 * case is scoped in the SQL" (routes.ts:558). So a supervisor holding
 * `timekeeping:manage` and not `timekeeping:read_all` gets `200` with their own
 * worker's corrections plus those of the workers ASSIGNED to them, under a
 * heading that reads as the organization's queue. "No corrections pending" then
 * means "none among the people you supervise", and the reader has no way to tell.
 *
 * This mirrors `readsWholeOrganization` — routes.ts:155, over
 * `ORG_WIDE_TIMEKEEPING = ['timekeeping:read_all']` (routes.ts:76). It is a
 * LABEL and not a gate: nothing is hidden or revealed by it, and it is the same
 * bargain `readScope.ts` documents for credentials and supervision. That is what
 * separates it from F35, where re-derived scope decided what to SHOW.
 *
 * AND IT MIRRORS IT WITH THE SAME FUNCTION THE SERVER USES. The predicate is
 *
 *     ORG_WIDE_TIMEKEEPING.some((key) => can(principal.authz, key))
 *
 * — `can()`, at its default ORGANIZATION scope, not a membership test on a
 * flattened key list. This asked `has()`, which is `/auth/me`'s flattened list
 * with `scopeType` and `scopeId` already discarded, so a `timekeeping:read_all`
 * devolved to ONE PROGRAM read as `true` here and `false` on the server: the
 * client suppressed the scope note over a queue the SQL had narrowed. That is
 * the flattening defect, in the direction that does damage — an over-affirmed
 * sentence about the organization — and `ScopeReader.can` is the client's
 * mirror of the very function the route calls.
 *
 * An unresolved permission set answers `unknown`, never `organization` — "this
 * is the whole organization" is the claim that does damage when it is wrong,
 * and `can()` is default-deny, so it cannot tell an unread set from an unheld
 * key on its own.
 */
export function correctionsPopulation(reader: ScopeReader): Population {
  if (reader.isLoading || reader.isError) return UNKNOWN_POPULATION;
  if (reader.can('timekeeping:read_all')) return ORGANIZATION;
  return narrowedTo('you and the workers you supervise');
}

// --- reading a request ------------------------------------------------------

/** The four things a correction can ask for, in a reviewer's words. */
export const ADJUSTMENT_TYPE_LABELS: Record<timekeeping.AdjustmentType, string> = {
  add_missing_session: 'Add a session that was never recorded',
  edit_times: 'Change the times on a session',
  add_missing_punch: 'Add a clock event that was never recorded',
  void_session: 'Void a session recorded in error',
};

/**
 * The heading for one request.
 *
 * An unrecognised `requestType` gets the generic sentence rather than the raw
 * enum value: the four are a database CHECK constraint
 * (`tar_request_type_ck`), so a fifth means this client is older than the API,
 * and printing `add_travel_time` at a supervisor helps nobody.
 */
export function describeAdjustment(requestType: string): string {
  return (
    ADJUSTMENT_TYPE_LABELS[requestType as timekeeping.AdjustmentType] ?? 'Correct a time record'
  );
}

/**
 * What a decided request says about itself.
 *
 * `denied`, not `rejected` — see `AdjustmentStatus` in the api-client. A status
 * this client does not know is reported as unknown, never silently bucketed
 * with one it does.
 */
export function describeAdjustmentStatus(status: string): {
  word: string;
  tone: 'good' | 'look' | 'urgent' | 'unsure' | 'plain';
} {
  switch (status) {
    case 'pending':
      return { word: 'Pending', tone: 'look' };
    case 'approved':
      return { word: 'Approved', tone: 'good' };
    case 'denied':
      return { word: 'Denied', tone: 'plain' };
    case 'cancelled':
      return { word: 'Withdrawn', tone: 'plain' };
    default:
      return { word: 'Status not recognized', tone: 'unsure' };
  }
}

// --- deciding ---------------------------------------------------------------

/**
 * The query key family every correction read lives under.
 *
 * Exported as the PREFIX so an invalidation reaches the pending queue, a
 * per-worker filter, and anything a later screen adds — react-query matches
 * `queryKey` prefixes. A decision that refreshed only the exact list it was
 * clicked from would leave the same request sitting `pending` on the next
 * screen over.
 */
export const CORRECTIONS_KEY = ['timekeeping', 'adjustments'] as const;

/** The pending-decision queue's own key. */
export const PENDING_CORRECTIONS_KEY = [...CORRECTIONS_KEY, 'pending'] as const;

// --- which corrections the queue is showing ---------------------------------

/**
 * THE QUEUE'S FILTER, AND WHY IT HAD TO EXIST.
 *
 * `GET /timekeeping/adjustments` takes a `status`, the repository ANDs it as a
 * parameter (`($2::text is null or status = $2)`), and every one of the four
 * values the column's CHECK allows is a legal answer. The only call site in the
 * product hard-coded `status: 'pending'`, so three of the four were unreachable
 * from any screen: a supervisor could decide a correction and never see it
 * again, nobody could answer "what happened to the request I filed on Tuesday",
 * and `describeAdjustmentStatus` — which renders all four — was rendering three
 * words no caller could produce.
 *
 * `all` sends NO status rather than four requests. The route's filter is
 * optional and the SQL treats a null as "every status", which is the same set
 * and one query.
 */
export type CorrectionFilter = 'pending' | 'approved' | 'denied' | 'cancelled' | 'all';

export const CORRECTION_FILTERS: readonly CorrectionFilter[] = [
  'pending',
  'approved',
  'denied',
  'cancelled',
  'all',
];

/**
 * The filter's name in a reviewer's words.
 *
 * `Withdrawn`, not `Cancelled`, matching {@link describeAdjustmentStatus} —
 * two words for one database value on one screen is how a reader concludes
 * they are two different things.
 */
export const CORRECTION_FILTER_LABELS: Record<CorrectionFilter, string> = {
  pending: 'Waiting on a decision',
  approved: 'Approved',
  denied: 'Denied',
  cancelled: 'Withdrawn',
  all: 'All corrections',
};

/**
 * The query key for one filter.
 *
 * Under {@link CORRECTIONS_KEY}, so `invalidateAfterDecision` still reaches
 * every one of them by prefix: deciding a correction moves it OUT of `pending`
 * and INTO `approved`, and refreshing only the list it was clicked from would
 * leave the other tab holding a row whose status it is now wrong about.
 */
export function correctionsQueryKey(filter: CorrectionFilter): readonly string[] {
  return [...CORRECTIONS_KEY, filter];
}

/** The query the API takes for one filter — `all` narrows by nothing. */
export function correctionsQuery(filter: CorrectionFilter): { status?: string } {
  return filter === 'all' ? {} : { status: filter };
}

/**
 * WHAT AN EMPTY LIST MEANS UNDER THIS FILTER.
 *
 * A measured zero, and it has to say WHICH zero. "No corrections pending" under
 * the Denied filter is a claim about a different set than the one the reader
 * asked for, and this screen already distinguishes an empty queue from a
 * refused one — collapsing the five filters into one sentence would put the
 * same defect back one level down.
 */
export function correctionsEmptyCopy(filter: CorrectionFilter): {
  heading: string;
  message: string;
} {
  switch (filter) {
    case 'pending':
      return {
        heading: 'No corrections pending',
        message:
          'When someone asks to change a time record — a worker on their phone, or an office user on their behalf — it appears here for a decision.',
      };
    case 'approved':
      return {
        heading: 'No corrections have been approved',
        message:
          'An approved correction rewrote a time record. None of the corrections you can see has been approved.',
      };
    case 'denied':
      return {
        heading: 'No corrections have been denied',
        message:
          'A denied correction left the record as it stood. None of the corrections you can see has been denied.',
      };
    case 'cancelled':
      return {
        heading: 'No corrections have been withdrawn',
        message:
          'A withdrawn correction was taken back by whoever filed it, before anybody decided it.',
      };
    case 'all':
      return {
        heading: 'No corrections at all',
        message:
          'Nobody has asked to change a time record among the workers you can see — not one waiting, decided or withdrawn.',
      };
  }
}

/**
 * The heading over the list.
 *
 * The card said "Time corrections waiting on a decision" unconditionally, which
 * was true while `pending` was the only reachable filter and is a false label
 * over a list of denied ones.
 */
export function correctionsHeading(filter: CorrectionFilter): string {
  switch (filter) {
    case 'pending':
      return 'Time corrections waiting on a decision';
    case 'approved':
      return 'Time corrections that were approved';
    case 'denied':
      return 'Time corrections that were denied';
    case 'cancelled':
      return 'Time corrections that were withdrawn';
    case 'all':
      return 'Time corrections';
  }
}

/**
 * MAY THIS PARTICULAR REQUEST BE DECIDED?
 *
 * `mayDecideCorrections` answers for the PRINCIPAL; this answers for the ROW.
 * `decideAdjustmentRequest` updates `where id = $1 and status = 'pending'` and
 * the service throws `AdjustmentError` on anything else, so a Decide control on
 * an already-approved correction is a button whose only outcome is a 409.
 *
 * It could not be wrong while the queue only ever held pending rows. It can now.
 */
export function isDecidable(request: { status: string }): boolean {
  return request.status === 'pending';
}

/**
 * MAY THIS READER WITHDRAW THIS PARTICULAR CORRECTION?
 *
 * Withdrawing is the requester taking back their own request, so the question
 * is not "what do you hold" but "did you file this". Three conditions, and each
 * one fails CLOSED:
 *
 *   1. The request is still `pending`. A decided correction cannot be
 *      un-decided; the route answers 409 and a control offering it would be a
 *      button whose only outcome is an error.
 *   2. `time:adjust` — the key that files one. A reader who cannot file cannot
 *      unfile.
 *   3. The reader IS `requestedByUserId`. An unresolved reader is `null` and a
 *      request with no recorded requester is `null`, and neither may match:
 *      `null === null` would offer every reader the withdraw control on every
 *      orphaned row while their own identity was still loading. So both nulls
 *      are refused explicitly rather than left to `===`.
 *
 * THE SERVER IS STILL THE DECISION. It re-reads `requested_by_user_id` under
 * the row lock and refuses regardless of what this function concluded. This
 * chooses what to OFFER; it does not grant anything.
 */
export function mayWithdrawCorrection(
  request: { status: string; requestedByUserId: string | null },
  readerUserId: string | null,
  has: (key: string | undefined) => boolean,
): boolean {
  if (!isDecidable(request)) return false;
  if (!mayFileCorrections(has)) return false;
  if (readerUserId === null || request.requestedByUserId === null) return false;
  return request.requestedByUserId === readerUserId;
}

/**
 * How a failed WITHDRAWAL should be rendered.
 *
 * The 403 here is "not yours to withdraw" and the 409 is "somebody decided it
 * while you were looking at it" — the second is the interesting one, because
 * the correct thing to do about it is to REREAD the row, not to try again. It
 * is separated from the generic failure for that reason: `decisionFailure`
 * would call a 409 retryable and tell the reader nothing had changed, when in
 * fact the record HAS changed and their screen is the stale thing.
 */
export function withdrawFailure(err: unknown): DecisionFailure {
  if (isForbidden(err)) {
    const message = typeof err.message === 'string' && err.message.trim() ? err.message : '';
    return {
      kind: 'refused',
      message: message || 'A correction can only be withdrawn by the person who filed it.',
    };
  }
  if (err instanceof ApiError && err.status === 409) {
    return {
      kind: 'refused',
      message:
        'This correction is no longer waiting on a decision — somebody approved or denied it. Refresh the queue to see what was decided; it cannot be withdrawn now.',
    };
  }
  return {
    kind: 'failed',
    message: 'We couldn’t withdraw that correction just now. It is still waiting on a decision.',
  };
}

/**
 * What has to be refreshed once a correction is decided.
 *
 * APPROVING IS NOT A STATUS CHANGE. `approveAdjustmentRequest` inserts a
 * replacement `time_session` and its punches and supersedes the old row
 * (apps/api/src/domains/timekeeping/service.ts:892 and `applyEditTimes`), so the
 * worker's sessions, the timesheet built from them and the admin board's counts
 * are all stale the moment the request flips. Refreshing only the queue would
 * leave a supervisor looking at the hours they just corrected, unchanged.
 */
export const CORRECTION_DECISION_INVALIDATES: readonly (readonly string[])[] = [
  ['timekeeping', 'adjustments'],
  ['timekeeping', 'sessions'],
  ['timesheets'],
  ['admin-views'],
];

/** A minimal stand-in for the bit of react-query's client this needs. */
export interface Invalidator {
  invalidateQueries: (filters: { queryKey: readonly unknown[] }) => unknown;
}

/**
 * Refresh everything a decision changed. Called from the mutation's `onSuccess`
 * and unit-tested directly, so "the decision refetches" is a property of a
 * function rather than of a component nobody can render in this test tier.
 */
export function invalidateAfterDecision(qc: Invalidator): void {
  for (const key of CORRECTION_DECISION_INVALIDATES) {
    qc.invalidateQueries({ queryKey: key });
  }
}

/**
 * How a failed decision should be RENDERED.
 *
 * The three refusals this route can give — you lack reach over this worker, the
 * subject could not be resolved, you are the subject — are all 403s, all
 * permanent for this caller and this request, and all carry a server sentence
 * that says which. A retry button on any of them is a lie with a button on it
 * (`ListStates.tsx`, on the `refused` phase: "Asking again cannot change the
 * answer").
 *
 * Everything else — a 409 on an already-decided request, a 500, a dropped
 * connection — is genuinely retryable, and is NOT to be dressed up as a
 * permission problem or as a connectivity claim this client cannot support.
 */
export type DecisionFailure =
  { kind: 'refused'; message: string } | { kind: 'failed'; message: string };

const REFUSAL_FALLBACK =
  'Your account cannot decide this correction. Deciding one needs reach over the worker whose time it changes, and nobody may decide a correction about themselves.';

export function decisionFailure(err: unknown): DecisionFailure {
  if (isForbidden(err)) {
    // The server's own sentence, when it sent one. It distinguishes "not the
    // workers you supervise" from "a time adjustment cannot be decided by its
    // own subject worker", and those are different things for the reader to do
    // next.
    const message = typeof err.message === 'string' && err.message.trim() ? err.message : '';
    return { kind: 'refused', message: message || REFUSAL_FALLBACK };
  }
  return {
    kind: 'failed',
    message:
      'We couldn’t record that decision just now. The correction is unchanged — it has not been approved or denied.',
  };
}

/**
 * The body for a decision.
 *
 * `deny`, never `reject`: `decideAdjustmentSchema` is
 * `z.enum(['approve', 'deny'])` (schemas.ts:129) and the api-client's type said
 * `reject` until this lane fixed it. An empty or whitespace-only note is OMITTED
 * rather than sent as `''` — the server trims and the field is optional, so
 * sending an empty string records "the reviewer wrote nothing" as if it were a
 * note.
 */
export function decisionOutcome(
  decision: 'approve' | 'deny',
  note: string,
): timekeeping.AdjustmentDecisionInput {
  const trimmed = note.trim();
  return { decision, ...(trimmed ? { decisionNote: trimmed } : {}) };
}

// --- filing -----------------------------------------------------------------

/**
 * A correction being composed. Every instant is held as the raw
 * `<input type="datetime-local">` value — local wall time, no zone — and
 * converted exactly once, on the way out, by {@link toInstant}.
 */
export interface CorrectionDraft {
  workerId: string;
  requestType: timekeeping.AdjustmentType;
  reason: string;
  timeSessionId: string;
  /** `YYYY-MM-DDTHH:mm`, as the browser gives it. */
  requestedClockInAt: string;
  requestedClockOutAt: string;
}

export const EMPTY_CORRECTION_DRAFT: CorrectionDraft = {
  workerId: '',
  requestType: 'edit_times',
  reason: '',
  timeSessionId: '',
  requestedClockInAt: '',
  requestedClockOutAt: '',
};

/**
 * A `datetime-local` value → the ISO instant the API demands.
 *
 * `requestedClockInAt` / `requestedClockOutAt` are
 * `z.string().datetime({ offset: true })` (schemas.ts:18,52), which requires a
 * zone designator. A `datetime-local` input hands over `2026-08-22T09:15` with
 * none, so posting the field verbatim is a 400 every time. `new Date(value)`
 * reads that as the VIEWER'S local wall time — which is what the person typing
 * it meant — and `toISOString()` gives the same instant with `Z`.
 *
 * Returns null for blank or unparseable input, so a caller cannot accidentally
 * send `"Invalid Date"`.
 */
export function toInstant(localValue: string): string | null {
  const trimmed = localValue.trim();
  if (!trimmed) return null;
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * WHY THIS DRAFT CANNOT BE SENT YET, in the words the person composing it needs
 * — or null when it can.
 *
 * This mirrors `createAdjustmentSchema`'s superRefine (schemas.ts:56-118) case
 * for case, on purpose and with the citation, because the alternative is a form
 * that posts and reports the server's zod path (`requestedClockInAt`) at
 * somebody. Mirrored validation is a convenience; the SERVER remains the
 * decision, and a draft this function passes can still be refused.
 */
export function correctionProblem(draft: CorrectionDraft): string | null {
  if (!draft.workerId) return 'Choose whose time this correction is about.';
  if (!draft.reason.trim()) return 'Say why the record needs changing — the reason is required.';
  if (draft.reason.trim().length > 2000) {
    return 'The reason is longer than 2000 characters, which is more than the record will hold.';
  }

  const inAt = toInstant(draft.requestedClockInAt);
  const outAt = toInstant(draft.requestedClockOutAt);

  switch (draft.requestType) {
    case 'add_missing_session':
      if (!inAt) return 'A session that was never recorded needs the time it should have started.';
      break;
    case 'edit_times':
      if (!draft.timeSessionId) return 'Changing times needs the session being corrected.';
      if (!inAt && !outAt) return 'Give the corrected clock-in, the corrected clock-out, or both.';
      break;
    case 'add_missing_punch':
      if (!draft.timeSessionId) return 'Adding a clock event needs the session it belongs to.';
      if (!inAt && !outAt) {
        return 'Give the missing instant — as a clock-in if the punch was a clock-in, as a clock-out if it was a clock-out.';
      }
      break;
    case 'void_session':
      if (!draft.timeSessionId) return 'Voiding needs the session being voided.';
      break;
  }

  if (inAt && outAt && new Date(outAt) < new Date(inAt)) {
    return 'The clock-out is before the clock-in.';
  }
  return null;
}

export function canSubmitCorrection(draft: CorrectionDraft): boolean {
  return correctionProblem(draft) === null;
}

/**
 * The draft as the API's body.
 *
 * OMITS what was not filled in rather than sending empty strings: `timeSessionId`
 * is `uuid.optional()` and `''` is not a uuid, so a blank field posted verbatim
 * turns a valid `add_missing_session` into a 400. The times go through
 * {@link toInstant}; a field the current request type does not use is dropped,
 * because a stray `requestedClockOutAt` left over from switching the type would
 * be stored on the request and read later as something the filer asked for.
 *
 * Call {@link canSubmitCorrection} first — this shapes a body, it does not
 * vouch for one.
 */
export function correctionPayload(draft: CorrectionDraft): timekeeping.CreateAdjustmentInput {
  const inAt = toInstant(draft.requestedClockInAt);
  const outAt = toInstant(draft.requestedClockOutAt);
  const usesSession = draft.requestType !== 'add_missing_session';
  const usesTimes = draft.requestType !== 'void_session';

  return {
    workerId: draft.workerId,
    requestType: draft.requestType,
    reason: draft.reason.trim(),
    ...(usesSession && draft.timeSessionId.trim()
      ? { timeSessionId: draft.timeSessionId.trim() }
      : {}),
    ...(usesTimes && inAt ? { requestedClockInAt: inAt } : {}),
    ...(usesTimes && outAt ? { requestedClockOutAt: outAt } : {}),
  };
}

/**
 * How a failed FILING should be rendered — same three-way distinction as
 * {@link decisionFailure}, different sentences.
 *
 * The 403 here means "not your own worker, not one you supervise, and you do not
 * hold `timekeeping:write_all`" (routes.ts:516). That is a fact about the
 * caller's reach, not about the form, and no amount of re-filling it will help.
 */
export function filingFailure(err: unknown): DecisionFailure {
  if (isForbidden(err)) {
    const message = typeof err.message === 'string' && err.message.trim() ? err.message : '';
    return {
      kind: 'refused',
      message:
        message ||
        'Your account cannot file a correction about this worker. Filing one is a claim about their hours, so it needs the same reach as recording their time.',
    };
  }
  return {
    kind: 'failed',
    message: 'We couldn’t file that correction just now. Nothing has been recorded.',
  };
}
